import * as amplitude from '@amplitude/analytics-browser';
import { EnrichmentPlugin, Event } from '@amplitude/analytics-types';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { AmplitudeAutoTrackEvents } from '@/components/Analytics/amplitude.types';
import { AmplitudeCatalogStatus, AmplitudeLotStatus } from '@/components/Analytics/Plugins/plugins.type';
import { getAmplitudeCatalogStatus, getAmplitudeItemStatus } from '@/components/Analytics/Plugins/plugins.utils';
import { useAppSelector } from '@/redux/hooks';
import { getCatalogIsTimedPlusBiddingOpen } from '@/redux/modules/catalog/catalogs/catalog.selectors';
import {
    getItemSummaryBuyNowStatus,
    getItemSummaryCatalogId,
    getItemSummaryCatalogStatus,
    getItemSummaryIsPassed,
    getItemSummaryIsPaused,
    getItemSummaryIsSold,
    getItemSummaryIsTimedPlusAuction,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { AmplitudePageName, getAmplitudePageNameFromPath } from '@/utils/getPageNameFromPath';
import { getItemIdFromPath } from '@/utils/urls';

/**
 * A plugin that adds Catalog- and Lot-related fields to the auto-tracked page view events of the item page
 */
class ItemPagePropertiesPlugin implements EnrichmentPlugin {
    name = 'lot-page-properties-plugin';
    type = 'enrichment' as const;

    private catalogStatus = AmplitudeCatalogStatus.None;
    private lotStatus = AmplitudeLotStatus.None;

    /**
     * Provide the lot and catalog information from the page, so that when the event is run, the data is available
     * @param catalogStatus - What is the status of the catalog?
     * @param lotStatus - What is the status of the lot?
     */
    addLotInfo = (catalogStatus: typeof this.catalogStatus, lotStatus: typeof this.lotStatus) => {
        this.catalogStatus = catalogStatus;
        this.lotStatus = lotStatus;
    };

    /**
     * <em>This function should not be called directly.</em>
     *
     * This is called by amplitude itself when any event is triggered and the plugin is registered
     *
     * @param event - The amplitude event that was triggered. Could be an auto-track event or one of our own defined events.
     * In the case of this plugin, we filter out anything that is not an auto-tracked page view of the item page.
     */
    execute = async (event: Event) => {
        const autoTrackUrl = event.event_properties?.['[Amplitude] Page URL'];
        if (autoTrackUrl) {
            const pageName = getAmplitudePageNameFromPath(autoTrackUrl);
            if (event.event_type === AmplitudeAutoTrackEvents.PageViewed && pageName === AmplitudePageName.LotDetails) {
                event.event_properties['Catalog Status'] = this.catalogStatus;
                event.event_properties['Lot Status'] = this.lotStatus;
            }
        }
        return event;
    };
}

/**
 * <em>This should only be used inside the AmplitudeProvider.</em>
 *
 * A hook that handles the initialization, usage, and cleanup of the ItemPagePropertiesPlugin.
 */
export const useItemPagePropertiesPlugin = () => {
    const { pathname } = useLocation();
    const [currentItemPagePlugin, setCurrentItemPagePlugin] = useState('');

    const itemId = getItemIdFromPath(pathname);
    const buyNowStatus = useAppSelector((state) => getItemSummaryBuyNowStatus(state, itemId));
    const catalogId = useAppSelector((state) => getItemSummaryCatalogId(state, itemId));
    const catalogStatus = useAppSelector((state) => getItemSummaryCatalogStatus(state, itemId));
    const isPassed = useAppSelector((state) => getItemSummaryIsPassed(state, itemId));
    const isSold = useAppSelector((state) => getItemSummaryIsSold(state, itemId));
    const isTimedPlusAuction = useAppSelector((state) => getItemSummaryIsTimedPlusAuction(state, itemId));
    const isPaused = useAppSelector((state) => getItemSummaryIsPaused(state, itemId));

    const isTimedPlusBiddingOpen = useAppSelector((state) => getCatalogIsTimedPlusBiddingOpen(state, catalogId));

    useEffect(() => {
        // we have moved to a new page, remove old plugin
        if (currentItemPagePlugin) {
            amplitude.remove(currentItemPagePlugin);
        }

        // we only add a new instance of the plugin to amplitude on the item page
        const pageName = getAmplitudePageNameFromPath(pathname);
        const amplitudeCatalogStatus = getAmplitudeCatalogStatus(catalogStatus);
        if (
            pageName === AmplitudePageName.LotDetails &&
            Boolean(itemId && catalogId) &&
            Boolean(amplitudeCatalogStatus)
        ) {
            // instantiate the plugin; this is not yet added to amplitude, so we can modify it further
            const plugin = new ItemPagePropertiesPlugin();
            // pass in the required information to our plugin. In this case, the catalog/item statuses
            plugin.addLotInfo(
                amplitudeCatalogStatus,
                getAmplitudeItemStatus(
                    buyNowStatus,
                    isPassed,
                    isSold,
                    isTimedPlusAuction,
                    isPaused,
                    catalogStatus,
                    isTimedPlusBiddingOpen
                )
            );
            // add the completed plugin to amplitude
            amplitude.add(plugin);
            // store the name for when we need to remove it
            setCurrentItemPagePlugin(plugin.name);
        }
    }, [
        buyNowStatus,
        catalogId,
        catalogStatus,
        currentItemPagePlugin,
        isPassed,
        isPaused,
        isSold,
        isTimedPlusAuction,
        isTimedPlusBiddingOpen,
        itemId,
        pathname,
    ]);
};
